import {
  type DefaultBlockSchema,
  type PartialBlock,
} from '@packages/blocknote-core'
import classNames from 'classnames'
import { ChevronRightIcon } from '@heroicons/react/20/solid'
import { Disclosure } from '@headlessui/react'
import { useState } from 'react'
import { useLocalStorage } from 'usehooks-ts'
import { pluralize } from '../../utils/commons'
import { type FromNote } from '../../providers/ReferencesContext'
import { useIsOnline } from '../../providers/NetworkProvider'
import { UniversalItemRenderer } from '../../components/shared/BlockRenderer'
import {
  createRenderableItem,
} from '../../components/shared/BlockRenderer/utils'


// Component for rendering a partial block
function ReferenceBlock({
  recordName,
  fromNote,
  line,
  onSelectNote: _onSelectNote,
  keyword,
}: {
  recordName: string
  fromNote: FromNote
  line: {
    index: number
    content: string
    block: PartialBlock<DefaultBlockSchema>
  }
  onSelectNote: () => void
  keyword?: string
}) {
  const renderableItem = createRenderableItem('block', line.block, {
    recordName,
    noteType: fromNote.noteType,
    lineIndex: line.index,
    expectedContent: line.content,
    keyword,
  })

  return (
    <UniversalItemRenderer
      item={renderableItem}
      variant='reference'
      showTaskToggle={true}
    />
  )
}

function NoteReferencedFrom({
  id,
  note,
  onSelectNote,
}: {
  id: string
  note: FromNote
  onSelectNote: (_recordName: string) => void
}) {
  const [isOpen, setIsOpen] = useState(true)

  return (
    <Disclosure as='div' defaultOpen={isOpen} className='mt-2'>
      {({ open }) => (
        <>
          <Disclosure.Button
            onClick={() => {
              setIsOpen(!isOpen)
            }}
            className='flex'
          >
            <ChevronRightIcon
              className={classNames({
                'rotate-90': open,
                'mr-1 h-4 w-4 shrink-0 text-gray-500 dark:text-gray-200': true,
              })}
              aria-hidden='true'
            />
            <span className='flex items-center justify-between text-xs font-bold'>
              {note.title}
            </span>
          </Disclosure.Button>
          <Disclosure.Panel className='ml-10'>
            {note.lines.map((line, index) => (
              <ReferenceBlock
                key={index}
                recordName={id}
                fromNote={note}
                line={line}
                onSelectNote={() => {
                  onSelectNote(id)
                }}
              />
            ))}
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  )
}

// Props for the NoteReference component
type NoteReferenceComponentProps = {
  references: Map<string, FromNote>
  onSelectNote: (_recordName: string) => void
}

export function NoteReference({
  references,
  onSelectNote,
}: NoteReferenceComponentProps) {
  const [isOpen, setIsOpen] = useLocalStorage('showReferences', true)
  const notes = [...references.entries()]
    .map(([recordName, note]) => ({ recordName, note }))
    .sort((a, b) => a.note.title.localeCompare(b.note.title))
  const isOnline = useIsOnline()

  return (
    <div className={classNames('relative', { 'opacity-10': !isOnline })}>
      <Disclosure
        as='div'
        defaultOpen={isOpen}
        className='blocknote-padding mx-auto mt-3 w-full max-w-3xl cursor-default'
      >
        {({ open }) => (
          <>
            <Disclosure.Button
              as='h1'
              className='flex items-center text-xs font-bold uppercase opacity-60'
              onClick={() => {
                setIsOpen(!isOpen)
              }}
            >
              <ChevronRightIcon
                className={classNames({
                  'rotate-90': open,
                  'mr-1 h-4 w-4 shrink-0 text-gray-500 dark:text-gray-200':
                    true,
                })}
                aria-hidden='true'
              />
              <span className='opacity-60'>
                {references.size} {pluralize('Reference', references.size)}
              </span>
            </Disclosure.Button>
            <Disclosure.Panel className='my-3 rounded-md bg-zinc-100/70 px-0 py-1.5 dark:bg-zinc-800'>
              <div>
                {notes.map(
                  (item) =>
                    item.note.lines.length > 0 && (
                      <NoteReferencedFrom
                        key={item.recordName}
                        id={item.recordName}
                        note={item.note}
                        onSelectNote={onSelectNote}
                      />
                    )
                )}
              </div>
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>
      {!isOnline && <div className='absolute left-0 top-0 h-full w-full' />}
    </div>
  )
}
